import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Home as HomeIcon, Sparkles, Users, BookOpen, Compass } from "lucide-react";
import { Toaster } from "sonner";
import { endpoints } from "@/lib/api";
import Onboarding from "@/components/Onboarding";
import useReminders from "@/hooks/useReminders";
import NotificationsTray from "@/components/NotificationsTray";
import BrandMark from "@/components/BrandMark";
import SiteFooter from "@/components/SiteFooter";
import SoulRitualToast from "@/components/SoulRitualToast";
import AccountMenu from "@/components/AccountMenu";

const PROC_PHOTOS_EVENT = "aging:proc-photos-changed";

/**
 * Primary destinations — five items only. Reflects the app's journey:
 *   Home       → Today's focus
 *   Becoming   → Discover · Understand · Choose (Soul Compass, Soul Work, Milestones)
 *   Rituals    → Practice (Mind / Body / Soul)
 *   Connection → Grow together (Collective / Circle / Wins)
 *   Journal    → Reflect
 *
 * Profile, Tracking, Moderation, and Wellness surfaces live behind the avatar
 * menu (see AccountMenu) so the primary bar stays calm.
 */
const TABS = [
  { to: "/",          label: "Home",       icon: HomeIcon,  testid: "nav-home" },
  { to: "/becoming",  label: "Becoming",   icon: Compass,   testid: "nav-becoming" },
  { to: "/rituals",   label: "Rituals",    icon: Sparkles,  testid: "nav-rituals" },
  { to: "/community", label: "Connection", icon: Users,     testid: "nav-community" },
  { to: "/journal",   label: "Journal",    icon: BookOpen,  testid: "nav-journal" },
];

export default function Layout() {
  const loc = useLocation();
  const active = TABS.find((t) => t.to === loc.pathname) || TABS[0];
  const [profile, setProfile] = useState(null);
  const [meta, setMeta] = useState(null);
  const [pulse, setPulse] = useState(0);
  const [unseenPhotos, setUnseenPhotos] = useState(0);
  useReminders();

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [loc.pathname]);

  const refreshUnseen = useCallback(async () => {
    try {
      const { data } = await endpoints.procedurePhotosUnseen();
      setUnseenPhotos(data.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refreshUnseen();
    const t = setInterval(refreshUnseen, 30000);
    const handler = () => refreshUnseen();
    window.addEventListener(PROC_PHOTOS_EVENT, handler);
    return () => { clearInterval(t); window.removeEventListener(PROC_PHOTOS_EVENT, handler); };
  }, [refreshUnseen]);

  // The procedure-photos badge lived on the primary Tracking tab before the
  // restructure. Tracking now lives in the account menu, so surface the
  // badge there instead by dispatching the count via a CSS variable read by
  // AccountMenu on next render (a small window event triggers a rerender).
  useEffect(() => {
    if (loc.pathname === "/tracking" && unseenPhotos > 0) {
      const t = setTimeout(refreshUnseen, 800);
      return () => clearTimeout(t);
    }
  }, [loc.pathname, unseenPhotos, refreshUnseen]);

  const loadShell = useCallback(async () => {
    const [p, m] = await Promise.all([endpoints.profile(), endpoints.meta()]);
    setProfile(p.data);
    setMeta(m.data);
  }, []);

  useEffect(() => { loadShell(); }, [loadShell]);

  const onOnboardingDone = async () => {
    await loadShell();
    setPulse((n) => n + 1);
  };

  return (
    <div className="App min-h-screen">
      <Toaster position="top-center" richColors closeButton />
      {profile && meta && <Onboarding profile={profile} meta={meta} onDone={onOnboardingDone} />}

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[hsl(var(--bg))]/80 border-b hairline">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="leading-tight">
              <div className="font-display text-lg tracking-tight" data-testid="app-name">
                <BrandMark testid="brand-wordmark-header" />
              </div>
              <div
                data-testid="app-tagline"
                className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hidden sm:block"
              >
                The Art Of Becoming
              </div>
              <div
                data-testid="app-active-tab"
                className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] sm:hidden"
              >
                {active.label}
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.to === "/"}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `tab-link relative px-3 py-2 rounded-full text-sm ${
                    isActive
                      ? "bg-[hsl(var(--sand))] text-[hsl(var(--fg))]"
                      : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
            <div className="ml-2 flex items-center gap-2">
              <NotificationsTray />
              <AccountMenu profile={profile} />
            </div>
          </nav>
          <div className="md:hidden flex items-center gap-2">
            <NotificationsTray />
            <AccountMenu profile={profile} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 pb-28 md:pb-16" data-testid="main-content">
        <Outlet context={{ pulse, refetchShell: loadShell }} />
      </main>

      <SiteFooter />
      <SoulRitualToast />

      {/* Mobile bottom tab bar — five primary destinations */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t hairline bg-[hsl(var(--bg))]/95 backdrop-blur-xl"
        data-testid="mobile-nav"
      >
        <ul className="grid grid-cols-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <li key={t.to}>
                <NavLink
                  to={t.to}
                  end={t.to === "/"}
                  data-testid={`m-${t.testid}`}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center gap-1 py-2.5 text-[10px] tracking-wide uppercase ${
                      isActive ? "text-[hsl(var(--clay))]" : "text-[hsl(var(--muted-foreground))]"
                    }`
                  }
                >
                  <div className="relative">
                    <Icon size={18} strokeWidth={1.5} />
                  </div>
                  {t.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
